<template>
  <div class="time-wheel" :class="{ 'time-wheel--disabled': disabled }">
    <div class="time-wheel__overlay time-wheel__overlay--top"></div>
    <div class="time-wheel__overlay time-wheel__overlay--bottom"></div>
    <div class="time-wheel__highlight" :style="highlightStyle"></div>

    <div
      ref="scroller"
      class="time-wheel__scroller"
      @scroll.passive="onScroll"
      @wheel.prevent="onMouseWheel"
      @touchstart.passive="isTouching = true"
      @touchend.passive="isTouching = false"
      @touchcancel.passive="isTouching = false"
    >
      <div class="time-wheel__pad" :style="padStyle"></div>

      <button
        v-for="(item, index) in normalizedItems"
        :key="`${String(item.value)}::${index}`"
        type="button"
        class="time-wheel__item"
        :class="{ active: item.value === modelValue, 'has-caption': Boolean(item.caption) }"
        :style="itemStyle"
        :disabled="disabled || item.disabled"
        @click="pick(index)"
      >
        <span v-if="item.caption" class="time-wheel__caption">{{ item.caption }}</span>
        <span class="time-wheel__label">{{ item.label }}</span>
      </button>

      <div class="time-wheel__pad" :style="padStyle"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TimeWheel',
  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    rowHeight: {
      type: Number,
      default: 72,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    wheelThreshold: {
      type: Number,
      default: 220,
    },
    wheelCooldown: {
      type: Number,
      default: 140,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      scrollTimer: null,
      isTouching: false,
      wheelAccumulator: 0,
      wheelResetTimer: null,
      lastWheelStepAt: 0,
    };
  },
  computed: {
    normalizedItems() {
      return (Array.isArray(this.items) ? this.items : []).map((item) => ({
        value: item?.value,
        label: item?.label ?? String(item?.value ?? ''),
        caption: item?.caption || '',
        disabled: Boolean(item?.disabled),
      }));
    },
    itemStyle() {
      return {
        height: `${this.rowHeight}px`,
      };
    },
    padStyle() {
      return {
        height: `calc(50% - ${this.rowHeight / 2}px)`,
      };
    },
    highlightStyle() {
      return {
        height: `${this.rowHeight}px`,
      };
    },
  },
  watch: {
    modelValue() {
      this.$nextTick(() => {
        this.scrollToValue(this.modelValue, 'smooth');
      });
    },
    rowHeight() {
      this.$nextTick(() => {
        this.scrollToValue(this.modelValue, 'auto');
      });
    },
    normalizedItems: {
      deep: true,
      handler(next) {
        const firstEnabled = next.find((item) => !item.disabled);
        if (!firstEnabled) return;

        const exists = next.some((item) => item.value === this.modelValue && !item.disabled);
        if (!exists) {
          this.$emit('update:modelValue', firstEnabled.value);
          return;
        }

        this.$nextTick(() => {
          this.scrollToValue(this.modelValue, 'auto');
        });
      },
    },
  },
  mounted() {
    const firstEnabled = this.normalizedItems.find((item) => !item.disabled);
    if (firstEnabled && !this.normalizedItems.some((item) => item.value === this.modelValue)) {
      this.$emit('update:modelValue', firstEnabled.value);
    }

    this.$nextTick(() => {
      this.scrollToValue(this.modelValue || firstEnabled?.value, 'auto');
    });
  },
  beforeUnmount() {
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
    if (this.wheelResetTimer) clearTimeout(this.wheelResetTimer);
  },
  methods: {
    findClosestEnabledIndex(index) {
      if (!this.normalizedItems.length) return -1;
      if (!this.normalizedItems[index]?.disabled) return index;

      for (let offset = 1; offset < this.normalizedItems.length; offset += 1) {
        const up = index - offset;
        const down = index + offset;

        if (up >= 0 && !this.normalizedItems[up]?.disabled) return up;
        if (down < this.normalizedItems.length && !this.normalizedItems[down]?.disabled) return down;
      }

      return -1;
    },

    scrollToValue(value, behavior = 'smooth') {
      const index = this.normalizedItems.findIndex((item) => item.value === value);
      if (index < 0 || !this.$refs.scroller) return;

      this.$refs.scroller.scrollTo({
        top: index * this.rowHeight,
        behavior,
      });
    },

    pick(index) {
      if (this.disabled) return;

      const safeIndex = this.findClosestEnabledIndex(index);
      if (safeIndex < 0) return;

      const item = this.normalizedItems[safeIndex];
      this.$emit('update:modelValue', item.value);
      this.scrollToValue(item.value, 'smooth');
    },

    stepByDirection(direction) {
      const currentIndex = this.normalizedItems.findIndex((item) => item.value === this.modelValue);
      let nextIndex = currentIndex >= 0 ? currentIndex : 0;

      do {
        nextIndex += direction;
      } while (
        nextIndex >= 0 &&
        nextIndex < this.normalizedItems.length &&
        this.normalizedItems[nextIndex]?.disabled
      );

      nextIndex = Math.max(0, Math.min(nextIndex, this.normalizedItems.length - 1));
      nextIndex = this.findClosestEnabledIndex(nextIndex);
      if (nextIndex < 0) return;

      const item = this.normalizedItems[nextIndex];
      this.$emit('update:modelValue', item.value);
      this.scrollToValue(item.value, 'smooth');
    },

    onMouseWheel(event) {
      if (this.disabled || !this.normalizedItems.length) return;

      const now = Date.now();
      const isMouseWheel = event.deltaMode === 1 || Math.abs(event.deltaY) >= 100;
      const threshold = isMouseWheel ? this.wheelThreshold : Math.round(this.wheelThreshold * 1.15);
      const cooldown = isMouseWheel ? this.wheelCooldown : Math.round(this.wheelCooldown * 1.2);

      this.wheelAccumulator += event.deltaY;

      if (this.wheelResetTimer) clearTimeout(this.wheelResetTimer);
      this.wheelResetTimer = setTimeout(() => {
        this.wheelAccumulator = 0;
      }, 180);

      if (Math.abs(this.wheelAccumulator) < threshold) return;
      if (now - this.lastWheelStepAt < cooldown) return;

      const direction = this.wheelAccumulator > 0 ? 1 : -1;
      this.wheelAccumulator = 0;
      this.lastWheelStepAt = now;

      this.stepByDirection(direction);
    },

    onScroll() {
      if (this.disabled || !this.normalizedItems.length) return;
      if (this.scrollTimer) clearTimeout(this.scrollTimer);

      this.scrollTimer = setTimeout(() => {
        const scroller = this.$refs.scroller;
        if (!scroller) return;

        const index = Math.round(scroller.scrollTop / this.rowHeight);
        const bounded = Math.max(0, Math.min(index, this.normalizedItems.length - 1));
        const safeIndex = this.findClosestEnabledIndex(bounded);
        if (safeIndex < 0) return;

        const item = this.normalizedItems[safeIndex];
        this.$emit('update:modelValue', item.value);

        scroller.scrollTo({
          top: safeIndex * this.rowHeight,
          behavior: 'smooth',
        });
      }, this.isTouching ? 120 : 100);
    },
  },
};
</script>

<style scoped src="./TimeWheel.css"></style>